import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { PartySelectorComponent } from '../../shared/design-system/party-selector.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import {
  InsuranceApiService,
  InsurancePolicyDocumentTemplate,
  UpsertInsurancePolicyDocumentTemplatePayload
} from './insurance-api.service';

@Component({
  standalone: true,
  selector: 'ain-insurance-print-templates-management',
  imports: [
    AinPageComponent,
    AinStateComponent,
    PartySelectorComponent,
    FormsModule,
    InputTextModule,
    ButtonModule,
    CheckboxModule
  ],
  template: `
    <ain-page [title]="t('insurance.printTemplates.title')" [description]="t('insurance.printTemplates.description')">
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else {
        <section class="card">
          <div class="toolbar">
            <button pButton type="button" severity="secondary" [label]="t('insurance.actions.refresh')" (click)="loadTemplates()"></button>
            <button pButton type="button" severity="secondary" [label]="t('common.add')" (click)="resetDraft()"></button>
            <button pButton type="button" [label]="t('common.save')" [disabled]="saving() || !canSaveDraft()" (click)="saveDraft()"></button>
            @if (selectedTemplateId()) {
              <button pButton type="button" severity="secondary" [label]="t('insurance.policyDetail.draftPreview.activateTemplate')" [disabled]="saving()" (click)="activateDraft()"></button>
            }
          </div>

          <div class="layout">
            <div class="template-list">
              <h4>{{ t('insurance.policyDetail.draftPreview.savedTemplates') }}</h4>
              @if (templates().length === 0) {
                <p class="hint">{{ t('insurance.policyDetail.draftPreview.noTemplates') }}</p>
              } @else {
                <div class="list-grid">
                  @for (item of templates(); track item.id) {
                    <button type="button" class="template-item" [class.active]="selectedTemplateId() === item.id" (click)="selectTemplate(item)">
                      <span>{{ item.name }}</span>
                      @if (item.isActive) {
                        <small>{{ t('insurance.policyTemplate.status.active') }}</small>
                      }
                    </button>
                  }
                </div>
              }
            </div>
            <div class="template-editor">
              <label>
                <span>{{ t('insurance.products.fields.legalEntityId') }}</span>
                <ain-party-selector [(ngModel)]="draft.legalEntityId" />
              </label>
              <label>
                <span>{{ t('insurance.policyDetail.draftPreview.templateName') }}</span>
                <input pInputText [(ngModel)]="draft.name" />
              </label>
              <label>
                <span>{{ t('insurance.policyDetail.draftPreview.templateDescription') }}</span>
                <input pInputText [(ngModel)]="draft.description" />
              </label>
              <label class="wide">
                <span>{{ t('insurance.policyDetail.draftPreview.templateVariablesJson') }}</span>
                <textarea pInputText rows="4" [(ngModel)]="draft.variablesJson"></textarea>
              </label>
              <label class="wide">
                <span>{{ t('insurance.policyDetail.draftPreview.templateHtml') }}</span>
                <textarea pInputText rows="14" [(ngModel)]="draft.templateHtml"></textarea>
              </label>
              <label class="inline">
                <p-checkbox binary="true" [(ngModel)]="draft.isActive" inputId="templateIsActive"></p-checkbox>
                <span>{{ t('insurance.policyTemplate.status.active') }}</span>
              </label>
              <label class="wide">
                <span>{{ t('insurance.policyDetail.draftPreview.aiPrompt') }}</span>
                <textarea pInputText rows="3" [(ngModel)]="aiPrompt"></textarea>
              </label>
              <div class="actions">
                <button pButton type="button" severity="secondary" [label]="t('insurance.policyDetail.draftPreview.applyPrompt')" [disabled]="saving() || !aiPrompt.trim()" (click)="applyAiPrompt()"></button>
              </div>
            </div>
          </div>

          @if (error()) { <p class="error">{{ error() }}</p> }
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .card { border: 1px solid #e5e7eb; border-radius: 12px; padding: 12px; }
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 10px; }
    .layout { display: grid; gap: 12px; grid-template-columns: minmax(220px, 300px) minmax(0, 1fr); }
    .template-list h4 { margin: 0 0 8px; }
    .list-grid { display: grid; gap: 6px; }
    .template-item { text-align: start; border: 1px solid #e5e7eb; border-radius: 8px; background: #fff; padding: 8px; cursor: pointer; display: grid; gap: 4px; }
    .template-item.active { border-color: #3b82f6; background: #eff6ff; }
    .template-editor { display: grid; gap: 8px; }
    label { display: grid; gap: 6px; }
    .inline { display: flex; align-items: center; gap: 8px; }
    .wide { grid-column: 1 / -1; }
    .actions { display: flex; gap: 8px; flex-wrap: wrap; }
    .hint { color: #64748b; }
    .error { color: #b91c1c; }
    @media (max-width: 900px) {
      .layout { grid-template-columns: 1fr; }
    }
  `]
})
export class InsurancePrintTemplatesManagementComponent implements OnInit {
  private readonly api = inject(InsuranceApiService);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly error = signal('');
  readonly templates = signal<InsurancePolicyDocumentTemplate[]>([]);
  readonly selectedTemplateId = signal<string | null>(null);
  aiPrompt = '';
  draft: UpsertInsurancePolicyDocumentTemplatePayload = this.emptyDraft();

  ngOnInit(): void {
    this.loadTemplates();
  }

  loadTemplates(): void {
    this.loading.set(true);
    this.api.listPolicyDocumentTemplates().subscribe({
      next: rows => {
        this.templates.set(rows);
        if (rows.length > 0) {
          const selected = this.selectedTemplateId();
          const current = selected ? rows.find(item => item.id === selected) : rows[0];
          if (current) {
            this.selectTemplate(current);
          } else {
            this.resetDraft();
          }
        } else {
          this.resetDraft();
        }
        this.error.set('');
        this.loading.set(false);
      },
      error: () => {
        this.templates.set([]);
        this.resetDraft();
        this.error.set(this.t('insurance.states.loadError'));
        this.loading.set(false);
      }
    });
  }

  selectTemplate(item: InsurancePolicyDocumentTemplate): void {
    this.selectedTemplateId.set(item.id);
    this.draft = {
      legalEntityId: item.legalEntityId,
      name: item.name,
      description: item.description,
      templateHtml: item.templateHtml,
      variablesJson: item.variablesJson,
      isActive: item.isActive
    };
  }

  resetDraft(): void {
    this.selectedTemplateId.set(null);
    this.aiPrompt = '';
    this.draft = this.emptyDraft();
  }

  canSaveDraft(): boolean {
    return !!(this.draft.name.trim() && this.draft.templateHtml.trim() && (this.selectedTemplateId() || this.draft.legalEntityId.trim()));
  }

  saveDraft(): void {
    if (!this.canSaveDraft()) {
      return;
    }

    this.saving.set(true);
    const request$ = this.selectedTemplateId()
      ? this.api.updatePolicyDocumentTemplate(this.selectedTemplateId()!, this.draft)
      : this.api.createPolicyDocumentTemplate(this.draft);
    request$.subscribe({
      next: item => {
        this.saving.set(false);
        this.selectedTemplateId.set(item.id);
        this.selectTemplate(item);
        this.loadTemplates();
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('insurance.states.loadError'));
      }
    });
  }

  activateDraft(): void {
    const id = this.selectedTemplateId();
    if (!id) {
      return;
    }

    this.saving.set(true);
    this.api.activatePolicyDocumentTemplate(id).subscribe({
      next: () => {
        this.saving.set(false);
        this.loadTemplates();
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('insurance.states.loadError'));
      }
    });
  }

  applyAiPrompt(): void {
    const prompt = this.aiPrompt.trim();
    if (!prompt) {
      return;
    }
    this.draft.templateHtml = this.applyPromptToTemplate(this.draft.templateHtml, prompt);
    this.aiPrompt = '';
  }

  private applyPromptToTemplate(templateHtml: string, prompt: string): string {
    const lower = prompt.toLowerCase();
    let updated = templateHtml;
    if (lower.includes('h1') && !updated.includes('<h1>')) {
      updated = `<h1>{{policy.policyNumber}}</h1>\n${updated}`;
    }
    if (lower.includes('status') && !updated.includes('{{policy.status}}')) {
      updated += '\n<p>Status: {{policy.status}}</p>';
    }
    if (lower.includes('coverage') && !updated.includes('{{coverages.rowsHtml}}')) {
      updated += '\n<table><tbody>{{coverages.rowsHtml}}</tbody></table>';
    }
    return updated;
  }

  private emptyDraft(): UpsertInsurancePolicyDocumentTemplatePayload {
    return {
      legalEntityId: '',
      name: '',
      description: '',
      templateHtml: `<article>
  <h2>Policy {{policy.policyNumber}}</h2>
  <p>Product: {{policy.productName}}</p>
  <p>Status: {{policy.status}}</p>
  <p>Premium: {{policy.premiumAmount}} {{policy.currencyCode}}</p>
  <h3>Coverages</h3>
  <table>
    <thead><tr><th>Code</th><th>Name</th><th>Sum insured</th><th>Premium</th><th>Deductible</th></tr></thead>
    <tbody>{{coverages.rowsHtml}}</tbody>
  </table>
</article>`,
      variablesJson: '["policy.policyNumber","policy.status","policy.premiumAmount","coverages.rowsHtml"]',
      isActive: false
    };
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
